import React, { FormEvent, useCallback, useState } from 'react';

import ProMultipleChoiceInput from 'elements/forms/inputs/ProMultipleChoiceInput';
import ProSelectInput from 'elements/forms/inputs/ProSelectInput';
import { StyledRequestACallForm } from 'organisms/mightyPro/RequestACallForm.styles';
import ButtonPro from 'src/elements/buttons/Button.pro';
import ProTextInput from 'src/elements/forms/inputs/ProTextInput';
import getParam from 'src/functions/getParam';
import getCookie from 'src/functions/getCookie';

export interface RequestACallFormFields {
  userDescription: string;
  reference: string;
  annualRevenue: string;
  email: string;
  name: string;
  isQualifiedPro: boolean;
  utmSource: string;
  utmCampaign: string;
  utmMedium: string;
  utmContent: string;
  utmTerm: string;
  clickID: string;
  gaClientID: string;
  formId: string;
}

interface RequestACallFormProps {
  onSubmit: (fields: RequestACallFormFields) => void;
}

const userDescriptionOptions = [
  'I work for a brand exploring a community on our own mobile apps',
  'I work for an organization building a community and we want our own mobile apps',
  'I am a creator ready to bring my community, courses, events & business together on my own mobile apps'
];

const businessRevenueOptions = [
  'Under $100K',
  '$100K-$250K',
  '$250K-$500K',
  'Over $500K'
];

const RequestACallForm = ({ onSubmit }: RequestACallFormProps): JSX.Element => {
  const utmSource = getParam('utm_source') ?? '';
  const utmCampaign = getParam('utm_campaign') ?? '';
  const utmMedium = getParam('utm_medium') ?? '';
  const utmContent = getParam('utm_content') ?? '';
  const utmTerm = getParam('utm_term') ?? '';
  const clickID = utmSource === 'google' ? getParam('gclid') : getParam('msclkid');
  const gaClientID = getCookie('_ga')?.match('((\\d{2,}\\.\\d{2,}))')[0]
  const formId = 'Mighty Pro Lead Form';
  const [ submitted, setSubmitted ] = useState<boolean>(false)
  const [ formValues, updateFormValues ] = useState<RequestACallFormFields>({
    userDescription: '',
    reference: '',
    annualRevenue: '',
    email: '',
    name: '',
    isQualifiedPro: undefined,
    utmSource,
    utmCampaign,
    utmMedium,
    utmContent,
    utmTerm,
    clickID,
    gaClientID,
    formId
  });


  const onSubmitWrapper = useCallback((event: FormEvent) => {
    const isQualifiedPro = formValues.annualRevenue === '$250K-$500K' || formValues.annualRevenue === 'Over $500K';
    const formData = new FormData(event.target as any);
    formData.set('userDescription', formValues.userDescription);
    formData.set('isQualifiedPro', isQualifiedPro.toString());
    formData.set('utmSource', formValues.utmSource);
    formData.set('utmCampaign', formValues.utmCampaign);
    formData.set('utmContent', formValues.utmContent);
    formData.set('utmTerm', formValues.utmTerm);
    formData.set('clickID', formValues.clickID);
    formData.set('gaClientID', formValues.gaClientID);
    formData.set('formId', formValues.formId)

    // request necessary for Netlify to record the form submission
    fetch('/request-a-call-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString()
    }).finally(() => null);

    onSubmit({ ...formValues, isQualifiedPro });
    event.preventDefault();
  }, [ onSubmit, formValues ]);

  return (
    <StyledRequestACallForm id="gform_6"
                            onSubmit={onSubmitWrapper}
                            name="request-a-call">
      {/*Required to record submissions on Netlify*/}
      <input type="hidden" name="form-name" value="request-a-call" />

      <ProMultipleChoiceInput label="What best describes you?"
                              onSelect={(newValue) => updateFormValues(values => ({
                                ...values,
                                userDescription: newValue
                              }))}
                              submitted={submitted}
                              inputName="userDescription"
                              required
                              options={userDescriptionOptions}
                              value={formValues.userDescription} />

      <ProSelectInput
        label="What's your annual business revenue? We use your revenue to determine if Mighty Pro makes sense at this stage in your business."
        placeholder="Choose One"
        required
        inputName="annualRevenue"
        options={businessRevenueOptions}
        onSelect={(newValue) => updateFormValues(values => ({ ...values, annualRevenue: newValue }))} />

      <ProTextInput value={formValues.email}
                    type="email"
                    label="What's your business email address?"
                    placeholder="Enter your Business Email"
                    inputName="email"
                    required
                    onChange={(newValue) => updateFormValues(values => ({ ...values, email: newValue }))} />

      <ProTextInput value={formValues.reference}
                    label="How did you hear about Mighty Pro?"
                    inputName="reference"
                    required
                    onChange={(newValue) => updateFormValues(values => ({ ...values, reference: newValue }))} />

      <ProTextInput value={formValues.name}
                    label="Lastly, what's your name?"
                    placeholder="Enter your Name"
                    inputName="name"
                    required
                    onChange={(newValue) => updateFormValues(values => ({ ...values, name: newValue }))} />

      <input type="hidden" name="clickID" value={formValues.clickID} />
      <input type="hidden" name="gaClientID" value={formValues.gaClientID} />
      <input type="hidden" name="utmSource" value={formValues.utmSource} />
      <input type="hidden" name="utmCampaign" value={formValues.utmCampaign} />
      <input type="hidden" name="utmMedium" value={formValues.utmMedium} />
      <input type="hidden" name="utmContent" value={formValues.utmContent} />
      <input type="hidden" name="utmTerm" value={formValues.utmTerm} />
      <input type="hidden" name="formId" value={formValues.formId} />

      <ButtonPro type="submit" onClick={() => setSubmitted(true)} text="Submit Your Call Request" />
    </StyledRequestACallForm>
  );
};

export default RequestACallForm;
